import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react';
import { pureHueCss, rgb2css, type RGB } from './colorMath.js';

const clamp01 = (n: number): number => Math.max(0, Math.min(1, n));

/** Pointer + keyboard helper — translates pointer drags inside an element
 *  to a [0,1]² fractional position, and arrow keys to ±1% nudges. Used by
 *  both the SV plane (2D) and the linear sliders (1D). */
function useDragTrack(
  ref: React.RefObject<HTMLElement | null>,
  onChange: (xFrac: number, yFrac: number) => void,
  axis: 'xy' | 'x' | 'y',
): {
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void;
} {
  // Pin the latest onChange so the global pointermove listener — attached
  // to `window` for the duration of the drag so the user can leave the
  // track without losing capture — calls the right closure even if the
  // parent re-renders mid-drag.
  const cbRef = useRef(onChange);
  cbRef.current = onChange;

  const measure = useCallback(
    (clientX: number, clientY: number): { x: number; y: number } => {
      const el = ref.current;
      if (el === null) return { x: 0, y: 0 };
      const r = el.getBoundingClientRect();
      const x = clamp01((clientX - r.left) / Math.max(1, r.width));
      const y = clamp01((clientY - r.top) / Math.max(1, r.height));
      return { x, y };
    },
    [ref],
  );

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLElement>): void => {
      if (e.button !== 0) return;
      e.preventDefault();
      const m = measure(e.clientX, e.clientY);
      cbRef.current(axis === 'y' ? 0 : m.x, axis === 'x' ? 0 : m.y);
      const onMove = (ev: PointerEvent): void => {
        const m2 = measure(ev.clientX, ev.clientY);
        cbRef.current(axis === 'y' ? 0 : m2.x, axis === 'x' ? 0 : m2.y);
      };
      const onUp = (): void => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [axis, measure],
  );

  return { onPointerDown };
}

export interface SVPlaneProps {
  hue: number;
  s: number;
  v: number;
  onChange: (s: number, v: number) => void;
  ariaLabel?: string;
}

/** Saturation × Value plane — horizontal axis is saturation (0=white→1=hue),
 *  vertical axis is *darkness* (0=hue→1=black). The dot sits where the
 *  current colour lives and inherits the live colour as its core fill so
 *  the user gets an immediate read on whichever value they're approaching. */
export function SVPlane(props: SVPlaneProps): ReactNode {
  const { hue, s, v, onChange, ariaLabel } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  const { onPointerDown } = useDragTrack(
    ref,
    (xFrac, yFrac) => {
      onChange(xFrac, 1 - yFrac);
    },
    'xy',
  );

  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    const step = e.shiftKey ? 0.1 : 0.01;
    let ns = s;
    let nv = v;
    if (e.key === 'ArrowLeft') ns = clamp01(s - step);
    else if (e.key === 'ArrowRight') ns = clamp01(s + step);
    else if (e.key === 'ArrowUp') nv = clamp01(v + step);
    else if (e.key === 'ArrowDown') nv = clamp01(v - step);
    else if (e.key === 'Home') ns = 0;
    else if (e.key === 'End') ns = 1;
    else if (e.key === 'PageUp') nv = 1;
    else if (e.key === 'PageDown') nv = 0;
    else return;
    e.preventDefault();
    onChange(ns, nv);
  };

  // Compose the hue tint underneath two CSS gradients (white→hue across,
  // hue→black down) — this reproduces the canonical HSV plane without
  // needing a canvas or an SVG.
  const planeStyle: React.CSSProperties = {
    background: `linear-gradient(to bottom, transparent, #000),
                 linear-gradient(to right, #fff, ${pureHueCss(hue)})`,
  };

  // Compute a CSS colour for the dot's *core* — the live HSV colour at
  // this position. The plane behind animates with the hue slider, so the
  // dot reading the same colour gives perfect "where am I" feedback.
  const dotCore = `color-mix(in oklab, white ${String((1 - s) * 100)}%, ${pureHueCss(hue)})`;
  const dotShaded = `color-mix(in oklab, black ${String((1 - v) * 100)}%, ${dotCore})`;

  return (
    <div
      ref={ref}
      className="vq-color-picker__sv"
      data-slot="sv-plane"
      role="application"
      aria-label={ariaLabel ?? 'Saturation and brightness'}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      style={planeStyle}
    >
      <span
        className="vq-color-picker__sv-dot"
        aria-hidden="true"
        style={{
          left: `${String(s * 100)}%`,
          top: `${String((1 - v) * 100)}%`,
          background: dotShaded,
        }}
      />
    </div>
  );
}

export interface HueSliderProps {
  hue: number;
  onChange: (hue: number) => void;
  ariaLabel?: string;
}

/** Linear hue slider — 0° at the left, 360° at the right. The thumb lives
 *  at hue/360 fraction and inherits the pure hue at that position so the
 *  user can read "where am I" directly off the thumb. */
export function HueSlider(props: HueSliderProps): ReactNode {
  const { hue, onChange, ariaLabel } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  const { onPointerDown } = useDragTrack(
    ref,
    (xFrac) => {
      onChange(xFrac * 360);
    },
    'x',
  );
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    const step = e.shiftKey ? 10 : 1;
    let h = hue;
    if (e.key === 'ArrowLeft') h = (hue - step + 360) % 360;
    else if (e.key === 'ArrowRight') h = (hue + step) % 360;
    else if (e.key === 'Home') h = 0;
    else if (e.key === 'End') h = 359;
    else return;
    e.preventDefault();
    onChange(h);
  };
  return (
    <div
      ref={ref}
      className="vq-color-picker__hue"
      data-slot="hue-slider"
      role="slider"
      aria-label={ariaLabel ?? 'Hue'}
      aria-valuemin={0}
      aria-valuemax={360}
      aria-valuenow={Math.round(hue)}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
    >
      <span
        className="vq-color-picker__slider-thumb"
        aria-hidden="true"
        style={{ left: `${String((hue / 360) * 100)}%`, background: pureHueCss(hue) }}
      />
    </div>
  );
}

export interface AlphaSliderProps {
  rgb: RGB;
  alpha: number;
  onChange: (alpha: number) => void;
  ariaLabel?: string;
}

/** Linear alpha slider — gradient runs from transparent (left, where the
 *  underlying checkerboard is fully visible) to the opaque current colour
 *  (right). The thumb fill matches the live colour at that opacity so the
 *  thumb itself reads "this is what you'd get". */
export function AlphaSlider(props: AlphaSliderProps): ReactNode {
  const { rgb, alpha, onChange, ariaLabel } = props;
  const ref = useRef<HTMLDivElement | null>(null);
  const { onPointerDown } = useDragTrack(
    ref,
    (xFrac) => {
      onChange(xFrac);
    },
    'x',
  );
  const onKeyDown = (e: ReactKeyboardEvent<HTMLDivElement>): void => {
    const step = e.shiftKey ? 0.1 : 0.01;
    let a = alpha;
    if (e.key === 'ArrowLeft') a = clamp01(alpha - step);
    else if (e.key === 'ArrowRight') a = clamp01(alpha + step);
    else if (e.key === 'Home') a = 0;
    else if (e.key === 'End') a = 1;
    else return;
    e.preventDefault();
    onChange(a);
  };
  const opaque = rgb2css({ ...rgb, a: 1 });
  const transparent = rgb2css({ ...rgb, a: 0 });
  return (
    <div
      ref={ref}
      className="vq-color-picker__alpha"
      data-slot="alpha-slider"
      role="slider"
      aria-label={ariaLabel ?? 'Opacity'}
      aria-valuemin={0}
      aria-valuemax={1}
      aria-valuenow={Number(alpha.toFixed(2))}
      tabIndex={0}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      style={
        {
          '--vq-alpha-track': `linear-gradient(90deg, ${transparent} 0%, ${opaque} 100%)`,
        } as React.CSSProperties
      }
    >
      <span
        className="vq-color-picker__slider-thumb"
        aria-hidden="true"
        style={{ left: `${String(alpha * 100)}%`, background: rgb2css(rgb) }}
      />
    </div>
  );
}

export interface EyedropperButtonProps {
  /** Fires with the picked sRGB hex when the user samples a screen pixel.
   *  Always fires uppercase-free (`#rrggbb`). */
  onPick: (hex: string) => void;
  ariaLabel?: string;
}

interface EyeDropperResult {
  sRGBHex: string;
}
interface EyeDropperConstructor {
  new (): { open: () => Promise<EyeDropperResult> };
}

/** Pipette button — sits next to the sliders. Uses the modern `EyeDropper`
 *  Web API (Chromium-only at time of writing) which paints its own UI and
 *  is fully programmatic — *not* a system colour picker. The button
 *  hides itself entirely when the API isn't present rather than falling
 *  back to `<input type="color">`. */
export function EyedropperButton(props: EyedropperButtonProps): ReactNode {
  const { onPick, ariaLabel } = props;
  const [supported, setSupported] = useSafeState(false);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const Ctor = (window as unknown as { EyeDropper?: EyeDropperConstructor }).EyeDropper;
    setSupported(typeof Ctor === 'function');
  }, [setSupported]);

  if (!supported) return null;

  const onClick = async (): Promise<void> => {
    const Ctor = (window as unknown as { EyeDropper?: EyeDropperConstructor }).EyeDropper;
    if (typeof Ctor !== 'function') return;
    try {
      const dropper = new Ctor();
      const result = await dropper.open();
      onPick(result.sRGBHex.toLowerCase());
    } catch {
      // User dismissed (Esc) — nothing to do.
    }
  };

  return (
    <button
      type="button"
      className="vq-color-picker__eyedropper-btn"
      onClick={() => {
        void onClick();
      }}
      aria-label={ariaLabel ?? 'Sample colour from screen'}
    >
      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
        <path
          d="M11.3 2.2a1.7 1.7 0 0 1 2.5 2.5l-1.6 1.6 1 1-1 1-1-1-5 5c-.6.6-1.3.8-2 .9l-1.4.2.2-1.4c.1-.7.3-1.4.9-2l5-5-1-1 1-1 1 1 1.4-1.5z"
          fill="currentColor"
          opacity="0.85"
        />
      </svg>
    </button>
  );
}

/** Tiny `useState` wrapper that ignores updates after unmount — keeps the
 *  EyeDropper feature-detect from emitting React's "set state on
 *  unmounted" warning if the hook is unmounted before the effect lands. */
function useSafeState<T>(initial: T): [T, (next: T) => void] {
  const ref = useRef(initial);
  const aliveRef = useRef(true);
  const [, force] = useState(0);
  useEffect(() => {
    aliveRef.current = true;
    return () => {
      aliveRef.current = false;
    };
  }, []);
  const set = useCallback((next: T): void => {
    if (!aliveRef.current) return;
    if (ref.current === next) return;
    ref.current = next;
    force((n) => n + 1);
  }, []);
  return [ref.current, set];
}
